import { CACHE_TIME } from '@/api/client';
import type { QueryCriteria, ResponseType } from '@/api/types';
import { queryOptions } from '@tanstack/react-query';

import type { CrudEndpoints } from '../endpoints/createCrudEndpoints';

export function createQueryFactory<TCore, TFlat, TEx, TInfo>(
  entityKey: string,
  endpoints: CrudEndpoints<TCore, TFlat, TEx, TInfo>
) {
  return {
    all: () => [entityKey] as const,

    lists: () => [entityKey, 'list'] as const,

    listCore: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'core'] as const,
        queryFn: () => endpoints.getAllCore(criteria),
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    listFlat: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'flat'] as const,
        queryFn: () => endpoints.getAllFlat(criteria),
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    listHierarchy: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'hierarchy'] as const,
        queryFn: () => endpoints.getAllHierarchy(criteria),
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    listInfo: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'info'] as const,
        queryFn: () => endpoints.getAllInfo(criteria),
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    details: () => [entityKey, 'detail'] as const,

    detail: (id: string, resType: ResponseType = 'hierarchy') =>
      queryOptions({
        queryKey: [entityKey, 'detail', id, resType] as const,
        queryFn: () => endpoints.getById(id, resType),
        enabled: !!id,
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    detailCore: (id: string) =>
      queryOptions({
        queryKey: [entityKey, 'detail', id, 'core'] as const,
        queryFn: () => endpoints.getCoreById(id),
        enabled: !!id,
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    // No-op queries for Suspense hooks when id is undefined (e.g., "new" mode)
    detailNoop: () => ({
      queryKey: [entityKey, 'detail', 'noop'] as const,
      queryFn: () => Promise.resolve(null),
      staleTime: Infinity
    }),

    detailCoreNoop: () => ({
      queryKey: [entityKey, 'detailCore', 'noop'] as const,
      queryFn: () => Promise.resolve(null),
      staleTime: Infinity
    })
  };
}

// Export type helper for consumers
export type QueryFactory<TCore, TFlat, TEx, TInfo> = ReturnType<typeof createQueryFactory<TCore, TFlat, TEx, TInfo>>;
